import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export function FormattedTimestamp({
  timestamp,
  short = false,
  className,
  onClick,
  href
}: {
  timestamp: number
  short?: boolean
  className?: string
  onClick?: (e: React.MouseEvent) => void
  href?: string
}) {
  const time = dayjs(timestamp * 1000)
  const fullTimestamp = time.format('YYYY-MM-DD HH:mm:ss')

  if (href) {
    return (
      <a
        href={href}
        className={cn(className, 'cursor-pointer hover:underline')}
        title={fullTimestamp}
        onClick={onClick}
      >
        <FormattedTimestampContent timestamp={timestamp} short={short} />
      </a>
    )
  }

  return (
    <span
      className={cn(className, onClick && 'cursor-pointer hover:underline')}
      title={fullTimestamp}
      onClick={onClick}
    >
      <FormattedTimestampContent timestamp={timestamp} short={short} />
    </span>
  )
}

function FormattedTimestampContent({
  timestamp,
  short = false
}: {
  timestamp: number
  short?: boolean
}) {
  const { t } = useTranslation()
  const time = dayjs(timestamp * 1000)
  const now = dayjs()

  if (time.isAfter(now)) {
    return short
      ? t('{{timestamp, date}}', { timestamp: time.valueOf() })
      : t('Scheduled for {{timestamp, date}}', { timestamp: time.valueOf() })
  }

  const diffMonth = now.diff(time, 'month')
  if (diffMonth >= 2) {
    return t('{{timestamp, date}}', { timestamp: time.valueOf() })
  }

  const diffDay = now.diff(time, 'day')
  if (diffDay >= 1) {
    return short ? t('{{n}} d', { n: diffDay }) : t('{{n}} days ago', { n: diffDay })
  }

  const diffHour = now.diff(time, 'hour')
  if (diffHour >= 1) {
    return short ? t('{{n}} h', { n: diffHour }) : t('{{n}} hours ago', { n: diffHour })
  }

  const diffMinute = now.diff(time, 'minute')
  if (diffMinute >= 1) {
    return short ? t('{{n}} m', { n: diffMinute }) : t('{{n}} minutes ago', { n: diffMinute })
  }

  return t('just now')
}
